<template>
  <div>
    <b-container fluid>
      <b-row>
        <b-col>
          <h2>Editar Termo</h2>
        </b-col>
      </b-row>

      <!-- Loading State -->
      <b-row v-if="loading" class="mt-4">
        <b-col class="text-center">
          <b-spinner></b-spinner>
          <p class="mt-2">Carregando termo...</p>
        </b-col>
      </b-row>

      <!-- Form -->
      <b-row v-else-if="term" class="mt-4">
        <b-col cols="12" md="10" lg="8">
          <!-- Warning if not draft -->
          <b-alert v-if="term.status !== 'draft'" show variant="warning" class="mb-4">
            <h5 class="alert-heading">Atenção!</h5>
            <p>Este termo tem status "<strong>{{ getStatusLabel(term.status) }}</strong>" e não pode ser editado.</p>
            <p class="mb-0">Apenas termos com status "Rascunho" podem ser editados. Para fazer alterações, crie um novo termo.</p>
          </b-alert>

          <b-form @submit.prevent="submitForm">
            <!-- Term Info -->
            <b-alert show variant="info" class="mb-4">
              <strong>ID:</strong> {{ term.id }} |
              <strong>Categoria:</strong> {{ getCategoryLabel(term.category) }} |
              <strong>Status:</strong>
              <b-badge :variant="getStatusVariant(term.status)">
                {{ getStatusLabel(term.status) }}
              </b-badge>
            </b-alert>

            <!-- Body Field with CKEditor -->
            <b-form-group
              label="Conteúdo do Termo:"
              label-for="term-body"
              description="Edite o conteúdo do termo usando o editor abaixo."
            >
              <ckeditor
                id="term-body"
                :editor="editor"
                v-model="form.body"
                :config="editorConfig"
                :disabled="term.status !== 'draft'"
              ></ckeditor>
            </b-form-group>

            <!-- Validation Error -->
            <b-alert v-if="validationError" show variant="danger" class="mt-3">
              {{ validationError }}
            </b-alert>

            <!-- API Error -->
            <b-alert v-if="apiError" show variant="danger" class="mt-3">
              {{ apiError }}
            </b-alert>

            <!-- Success Message -->
            <b-alert v-if="successMessage" show variant="success" class="mt-3">
              {{ successMessage }}
            </b-alert>

            <!-- Submit Buttons -->
            <div class="mt-4">
              <b-button
                type="submit"
                variant="primary"
                :disabled="submitting || term.status !== 'draft'"
                class="mr-2"
              >
                <b-spinner small v-if="submitting"></b-spinner>
                {{ submitting ? 'Salvando...' : 'Salvar Alterações' }}
              </b-button>

              <b-button
                variant="secondary"
                :disabled="submitting"
                @click="goBack"
              >
                Voltar
              </b-button>
            </div>
          </b-form>
        </b-col>
      </b-row>

      <!-- Error Loading -->
      <b-row v-else class="mt-4">
        <b-col>
          <b-alert show variant="danger">
            Erro ao carregar termo. Por favor, tente novamente.
          </b-alert>
          <b-button variant="primary" @click="goBack">
            Voltar para Lista
          </b-button>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import CKEditor from '@ckeditor/ckeditor5-vue2'
import Editor from 'ckeditor5-custom-build/build/ckeditor'

export default {
  name: 'TermsEdit',
  components: {
    ckeditor: CKEditor.component
  },
  data () {
    return {
      editor: Editor,
      loading: true,
      term: null,
      form: {
        body: ''
      },
      editorConfig: {
        fontSize: {
          options: [
            { title: 'Tiny', model: '12px' },
            { title: 'Small', model: '13px' },
            'default',
            { title: 'Big', model: '20px' },
            { title: 'Huge', model: '23px' }
          ]
        },
        link: {
          addTargetToExternalLinks: true
        }
      },
      validationError: '',
      apiError: '',
      successMessage: '',
      submitting: false
    }
  },
  async mounted () {
    await this.fetchTerm()
  },
  methods: {
    async fetchTerm () {
      this.loading = true
      try {
        const termId = this.$route.params.id
        const response = await this.$http.get(
          process.env.VUE_APP_URL_APP + `/b/admin/terms/${termId}`
        )
        this.term = response.body
        this.form.body = this.term.body
      } catch (err) {
        console.error(err)
        this.apiError = 'Erro ao carregar termo.'
      } finally {
        this.loading = false
      }
    },
    getStatusVariant (status) {
      const variants = {
        draft: 'warning',
        published: 'success',
        deprecated: 'secondary'
      }
      return variants[status] || 'secondary'
    },
    getStatusLabel (status) {
      const labels = {
        draft: 'Rascunho',
        published: 'Publicado',
        deprecated: 'Depreciado'
      }
      return labels[status] || status
    },
    getCategoryLabel (category) {
      const labels = {
        purchase_terms: 'Termos de Compra'
      }
      return labels[category] || category
    },
    validateForm () {
      this.validationError = ''

      // Check if term can be edited
      if (this.term.status !== 'draft') {
        this.validationError = 'Apenas termos com status "Rascunho" podem ser editados.'
        return false
      }

      // Remove HTML tags to check if body has actual content
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = this.form.body
      const textContent = tempDiv.textContent || tempDiv.innerText || ''

      if (!textContent.trim()) {
        this.validationError = 'O conteúdo do termo não pode estar vazio.'
        return false
      }

      return true
    },
    async submitForm () {
      // Clear previous messages
      this.validationError = ''
      this.apiError = ''
      this.successMessage = ''

      // Validate form
      if (!this.validateForm()) {
        return
      }

      this.submitting = true

      try {
        const payload = {
          body: this.form.body
        }

        await this.$http.put(
          process.env.VUE_APP_URL_APP + `/b/admin/terms/${this.term.id}`,
          payload
        )

        this.successMessage = 'Termo atualizado com sucesso!'

        // Show success toast
        this.$bvToast.toast('As alterações foram salvas.', {
          title: 'Termo Atualizado',
          autoHideDelay: 5000,
          variant: 'success',
          toaster: 'b-toaster-top-center',
          solid: true
        })

        // Redirect to list after short delay
        setTimeout(() => {
          this.$router.push({ name: 'AdminTermsList' })
        }, 1500)
      } catch (err) {
        console.error(err)
        this.apiError = err.body?.message || 'Erro ao atualizar termo. Por favor, tente novamente.'
      } finally {
        this.submitting = false
      }
    },
    goBack () {
      this.$router.push({ name: 'AdminTermsList' })
    }
  }
}
</script>

<style scoped>
/* Ensure CKEditor has minimum height */
::v-deep .ck-editor__editable {
  min-height: 400px;
}
</style>
